// Designer <-> store matching, shared by the Add Item form and the camera
// intake so both follow the same auto-fill logic.

import type { Store } from '@/lib/types';

// Rough name-similarity score (0..1): exact match wins; substring is a strong
// match; otherwise a bigram Dice coefficient.
export function nameSimilarity(a: string, b: string): number {
  const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
  const x = norm(a);
  const y = norm(b);
  if (!x || !y) return 0;
  if (x === y) return 1;
  if (x.includes(y) || y.includes(x)) return 0.9;
  const bigrams = (s: string) => {
    const m = new Map<string, number>();
    for (let i = 0; i < s.length - 1; i++) {
      const g = s.slice(i, i + 2);
      m.set(g, (m.get(g) ?? 0) + 1);
    }
    return m;
  };
  const ma = bigrams(x);
  const mb = bigrams(y);
  let inter = 0;
  for (const [g, c] of ma) inter += Math.min(c, mb.get(g) ?? 0);
  const total = x.length - 1 + (y.length - 1);
  return total > 0 ? (2 * inter) / total : 0;
}

// Normalize a name OR a URL/domain to a comparable token so that
// "Mr Porter", "mrporter.com", and "www.MrPorter.com/..." all collapse to
// "mrporter".
export function normalizeName(s: string): string {
  return s
    .toLowerCase()
    .trim()
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\.(com|net|org|co|io|shop|store|us|co\.uk|fashion)(\/.*)?$/, '')
    .replace(/[^a-z0-9]/g, '');
}

export interface Aliasable {
  name: string;
  aliases?: string[] | null;
}

// Find an existing entry that the candidate name (or domain) most likely refers
// to: an exact normalized match on the name or any alias, else a high fuzzy
// score. Returns null when nothing is close enough.
export function findCloseMatch<T extends Aliasable>(candidate: string, items: T[]): T | null {
  const norm = normalizeName(candidate);
  if (!norm) return null;
  for (const it of items) {
    if (normalizeName(it.name) === norm) return it;
    if ((it.aliases ?? []).some((a) => normalizeName(a) === norm)) return it;
  }
  let best: T | null = null;
  let bestScore = 0;
  for (const it of items) {
    const score = Math.max(
      nameSimilarity(candidate, it.name),
      ...(it.aliases ?? []).map((a) => nameSimilarity(candidate, a)),
      0,
    );
    if (score > bestScore) {
      bestScore = score;
      best = it;
    }
  }
  return bestScore >= 0.82 ? best : null;
}

// Among stores linked to a designer, the one whose name best resembles it.
export function bestStoreForDesigner(stores: Store[], designerName: string): Store | null {
  const linked = stores.filter((s) =>
    (s.designers ?? []).some((d) => d.name.toLowerCase() === designerName.toLowerCase()),
  );
  if (linked.length === 0) return null;
  return linked.reduce((best, s) =>
    nameSimilarity(s.name, designerName) > nameSimilarity(best.name, designerName) ? s : best,
  );
}

// Among a store's linked designers, the one whose name best resembles the store.
export function bestDesignerForStore(store: Store): string | null {
  const linked = store.designers ?? [];
  if (linked.length === 0) return null;
  return linked.reduce((best, d) =>
    nameSimilarity(d.name, store.name) > nameSimilarity(best.name, store.name) ? d : best,
  ).name;
}
